
import React from 'react';
import { Space, Tag, Button, Tooltip, Image } from 'antd';
import { SearchOutlined, CalendarOutlined, ImportOutlined, AmazonOutlined } from '@ant-design/icons';
import { PRICE_RANGE_COLORS } from '../../constants';
import type { TreeNode } from '../../types';

interface UseTableColumnsProps {
    skuId?: number;
    importLoading: Record<string, boolean>;
    handleImport: (record: TreeNode) => void;
}

export const useTableColumns = ({ skuId, importLoading, handleImport }: UseTableColumnsProps) => {
    const columns = [
        {
            title: '分组 / 商品',
            dataIndex: 'name',
            key: 'name',
            width: 300,
            render: (text: string, r: TreeNode) => {
                if (r.nodeType === 'product') {
                    return (
                        <Space align="start">
                            <div style={{ width: 40, height: 40, border: '1px solid #f0f0f0', borderRadius: 4, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {r.image_url ? (
                                    <Image src={r.image_url} width={40} height={40} preview={{ mask: false }} />
                                ) : (
                                    <AmazonOutlined style={{ fontSize: 20, color: '#ccc' }} />
                                )}
                            </div>
                            <div>
                                <div style={{ fontSize: 12, lineHeight: '1.2', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', maxWidth: 200, marginBottom: 2 }} title={text}>
                                    <a href={`https://www.amazon.com/dp/${r.asin}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                                        {text}
                                    </a>
                                </div>
                                <Space size={4}>
                                    <Tag style={{ margin: 0, fontSize: 10, lineHeight: '16px' }}>ASIN: {r.asin}</Tag>
                                    {skuId && (
                                        <Tooltip title="添加监控">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<ImportOutlined />}
                                                loading={importLoading[r.key]}
                                                onClick={() => handleImport(r)}
                                                style={{ height: 20, padding: '0 4px', fontSize: 12, color: '#1890ff' }}
                                            />
                                        </Tooltip>
                                    )}
                                </Space>
                            </div>
                        </Space>
                    );
                }

                return (
                    <span style={{ fontWeight: r.nodeType === 'keyword' || r.nodeType === 'total' ? 'bold' : 'normal' }}>
                        {(r.nodeType === 'keyword' || r.nodeType === 'total') && (
                            <SearchOutlined style={{ marginRight: 8 }} />
                        )}
                        {r.nodeType === 'date' && <CalendarOutlined style={{ marginRight: 8, color: '#888' }} />}
                        {r.nodeType === 'priceRange' ? (
                            <Tag color={PRICE_RANGE_COLORS[text.replace('$', '')] || 'blue'}>{text}</Tag>
                        ) : (
                            text
                        )}
                    </span>
                );
            },
        },
        {
            title: 'Rating',
            dataIndex: 'avg_rating',
            key: 'avg_rating',
            width: 80,
            align: 'center' as const,
            render: (v: number | null, r: TreeNode) =>
                v !== null ? (
                    <span
                        style={{
                            color: v >= 4.5 ? '#52c41a' : v >= 4 ? '#1890ff' : '#faad14',
                            fontWeight: r.nodeType === 'keyword' ? 'bold' : 'normal',
                        }}
                    >
                        {v.toFixed(2)}
                    </span>
                ) : (
                    <span style={{ color: '#d9d9d9' }}>-</span>
                ),
        },
        {
            title: '上架天数',
            dataIndex: 'avg_days',
            key: 'avg_days',
            width: 85,
            align: 'center' as const,
            render: (v: number | null, r: TreeNode) =>
                v !== null && r.nodeType !== 'product' ? (
                    <span style={{ fontWeight: r.nodeType === 'keyword' ? 'bold' : 'normal' }}>{v}天</span>
                ) : (
                    <span style={{ color: '#d9d9d9' }}>-</span>
                ),
        },
        {
            title: '均价',
            dataIndex: 'avg_price',
            key: 'avg_price',
            width: 75,
            align: 'center' as const,
            render: (v: number | null, r: TreeNode) =>
                v !== null ? (
                    <span style={{ color: '#52c41a', fontWeight: r.nodeType === 'keyword' ? 'bold' : 'normal' }}>
                        ${v.toFixed(2)}
                    </span>
                ) : (
                    <span style={{ color: '#d9d9d9' }}>-</span>
                ),
        },
        {
            title: '品牌',
            dataIndex: 'brand_count',
            key: 'brand_count',
            width: 55,
            align: 'center' as const,
            render: (v: number, r: TreeNode) =>
                r.nodeType === 'product' ? <span style={{ color: '#d9d9d9' }}>-</span> :
                    v ? (
                        <span style={{ fontWeight: r.nodeType === 'keyword' ? 'bold' : 'normal' }}>{v}</span>
                    ) : (
                        <span style={{ color: '#d9d9d9' }}>-</span>
                    ),
        },
        {
            title: '销量',
            dataIndex: 'total_sales',
            key: 'total_sales',
            width: 110,
            align: 'right' as const,
            render: (v: number | null, r: TreeNode) =>
                v !== null ? (
                    <span style={{ fontWeight: r.nodeType === 'keyword' ? 'bold' : 'normal' }}>
                        {v.toLocaleString()}
                        {r.sales_pct !== undefined && (
                            <span style={{ color: '#888', fontSize: 11 }}> ({r.sales_pct}%)</span>
                        )}
                    </span>
                ) : (
                    <span style={{ color: '#d9d9d9' }}>-</span>
                ),
        },
        {
            title: '销售额',
            dataIndex: 'total_revenue',
            key: 'total_revenue',
            width: 120,
            align: 'right' as const,
            render: (v: number | null, r: TreeNode) =>
                v !== null ? (
                    <span style={{ color: '#fa541c', fontWeight: r.nodeType === 'keyword' ? 'bold' : 'normal' }}>
                        ${v.toLocaleString()}
                        {r.revenue_pct !== undefined && (
                            <span style={{ color: '#888', fontSize: 11 }}> ({r.revenue_pct}%)</span>
                        )}
                    </span>
                ) : (
                    <span style={{ color: '#d9d9d9' }}>-</span>
                ),
        },
        {
            title: '数量',
            dataIndex: 'result_count',
            key: 'result_count',
            width: 80,
            align: 'center' as const,
            render: (v: number, r: TreeNode) =>
                r.nodeType === 'product' ? <span style={{ color: '#d9d9d9' }}>-</span> :
                    v ? (
                        <span>
                            {v}
                            {r.count_pct !== undefined && (
                                <span style={{ color: '#888', fontSize: 11 }}> ({r.count_pct}%)</span>
                            )}
                        </span>
                    ) : (
                        ''
                    ),
        },
    ];

    return columns;
};
